import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import { Button, CommunityWordlistBadge, LinkButton } from 'src/components';
import { TrackingApi, WordlistsApi } from 'src/api';
import { WordlistWithProgress } from 'src/api/wordlists/types';
import { ROUTES } from 'src/constants/routes.const';
import { useExperienceContext } from 'src/context/experience';
import { useUserContext } from 'src/context/user';
import useSafePush from 'src/hooks/useSafePush';
import { getAprenderLearnSessionUrl } from 'src/utils/getAprenderLearnSessionUrl';
import { stringifyUrl } from 'src/utils/stringifyUrl';
import { saveActiveWordlistId } from 'src/utils/wordlists/saveActiveWordlist';
import {
  advanceToNextTrackWordlist,
  isWordlistComplete,
} from 'src/apps/VocabTracks/advanceToNextTrackWordlist';
import { useEligibility } from 'src/hooks/useEligibility';
import { vocabTrackMsgs } from 'src/apps/VocabTracks/messages';
import { LocalsVideoPreview } from './LocalsVideoPreview';
import { MediaPreviewPanel } from './MediaPreviewPanel';
import { PreviewLoadingSkeleton, PreviewMediaSkeleton } from './PreviewLoadingSkeleton';
import { PREVIEW_ANIMATION } from './PreviewLoadingSkeleton/constants';
import { useWordlistLocalsPreview } from './useWordlistLocalsPreview';
import * as S from './styles';

const msgs = defineMessages({
  currentlyLearning: {
    id: 'wordlists_currentlylearning_label',
  },
  startLesson: {
    id: 'scenario_button_start_lesson',
  },
  changeWordlist: {
    id: 'dashboard_card_change_wordlist_short_cta',
    defaultMessage: 'Change',
  },
  wordsLearnt: {
    id: 'dashboard_next_lesson_words_learnt',
    defaultMessage: '{learned} of {total} words learnt',
  },
  listProgressLabel: {
    id: 'dashboard_next_lesson_list_progress_label',
    defaultMessage: 'Words learnt in {title}',
  },
  practiceVocabTitle: {
    id: 'dashboard_current_wordlist_practice_title',
    defaultMessage: 'Practice your vocab',
  },
  learningWordlist: {
    id: 'dashboard_current_wordlist_learning_label',
    defaultMessage: "You're learning <bold>{title}</bold>",
  },
});

export type CurrentWordlistCardProps = {
  languagePairId?: number;
  showPracticeVocabHeader?: boolean;
  /**
   * When this card is the Path's next vocab step, include the node id so
   * finishing the lesson marks that node complete — same as starting from /path.
   */
  pathwayNodeId?: number;
};

const hasWordlistProgress = (
  wordlist: { learnablesCount: number } | WordlistWithProgress,
): wordlist is WordlistWithProgress => 'learnedLearnablesCount' in wordlist;

export const CurrentWordlistCard: FC<CurrentWordlistCardProps> = ({
  languagePairId,
  showPracticeVocabHeader,
  pathwayNodeId,
}) => {
  const intl = useIntl();
  const { safePush } = useSafePush();
  const queryClient = useQueryClient();
  const { experienceType } = useExperienceContext();
  const { user } = useUserContext();
  const { isEligibleForTracksAndThemes: useVocabTracks } = useEligibility(languagePairId);

  const trackPreviewAction = useCallback(
    (
      action: 'wordlist_preview_shown' | 'clicked_wordlist_preview_item',
      trackingData: Record<string, string>,
    ) => {
      /* istanbul ignore next: analytics */
      void TrackingApi.trackUserAction(
        action,
        'dashboard_home_tab',
        languagePairId ?? null,
        !!user?.isPro,
        { card: 'current_wordlist', ...trackingData },
      );
    },
    [languagePairId, user?.isPro],
  );

  const handleCardClick = () => {
    /* istanbul ignore next: analytics */
    void TrackingApi.trackUserAction(
      'clicked_home_card',
      'dashboard_home_tab',
      languagePairId ?? null,
      !!user?.isPro,
      { card: 'current_wordlist' },
    );
  };

  const { data: wordlistsData } = useQuery({
    queryKey: ['getWordlistsWithProgress', languagePairId],
    queryFn: () => WordlistsApi.getWordlistsWithProgress(languagePairId as number),
    enabled: !!languagePairId,
  });

  const activeWordlistFromList = useMemo(() => {
    if (!wordlistsData) return null;
    return wordlistsData.wordlists.find(w => w.wordlistId === wordlistsData.activeWordlistId);
  }, [wordlistsData]);

  const activeWordlistId = wordlistsData?.activeWordlistId;

  // Dashboard load (and React Query's focus refetch) is the early/post-auth and
  // foreground refresh point for the reserved `active_wordlist_id` tracking key.
  useEffect(() => {
    if (wordlistsData) {
      saveActiveWordlistId(wordlistsData.activeWordlistId);
    }
  }, [wordlistsData]);

  const shouldFetchWordlist = !!languagePairId && !!activeWordlistId && !activeWordlistFromList;

  const { data: wordlistData } = useQuery({
    queryKey: ['getWordlist', languagePairId, activeWordlistId],
    queryFn: () => WordlistsApi.getWordlist(languagePairId as number, activeWordlistId as string),
    enabled: shouldFetchWordlist,
  });

  const activeWordlist = activeWordlistFromList ?? wordlistData?.wordlist;

  // Only the collection response carries the track order an advance needs, so a
  // list fetched on its own never triggers one.
  const isActiveWordlistComplete =
    !!activeWordlistFromList && isWordlistComplete(activeWordlistFromList);
  const [isAdvancingWordlist, setIsAdvancingWordlist] = useState(false);

  const handleStartLesson = useCallback(async () => {
    if (experienceType !== '2025' || !languagePairId) {
      void safePush(ROUTES.scenarios.learn);
      return;
    }

    const learnSessionUrl = getAprenderLearnSessionUrl(
      languagePairId,
      pathwayNodeId !== undefined
        ? { source: 'current_wordlist_card', pathwayNodeId }
        : 'current_wordlist_card',
    );

    if (!useVocabTracks || !isActiveWordlistComplete) {
      void safePush(learnSessionUrl);
      return;
    }

    // The finished list has nothing left to teach, so Learn would fail. Move the
    // learner on to the next list in their track before launching, and send them
    // to the picker only once the whole track is done.
    setIsAdvancingWordlist(true);
    try {
      const result = await advanceToNextTrackWordlist(languagePairId, queryClient);
      void safePush(
        result === 'track-complete' ? ROUTES.wordlists.changeVocabTrack : learnSessionUrl,
      );
    } catch {
      void safePush(learnSessionUrl);
    }
  }, [
    experienceType,
    isActiveWordlistComplete,
    languagePairId,
    pathwayNodeId,
    queryClient,
    safePush,
    useVocabTracks,
  ]);

  const showPreviewUi = experienceType === '2025' && !!activeWordlist;
  const { learnables: previewLearnables, isLoading: isPreviewLoading } = useWordlistLocalsPreview({
    languagePairId,
    enabled: showPreviewUi,
  });

  const hasPreview = previewLearnables.length > 0;
  const [selectedPreviewId, setSelectedPreviewId] = useState<number | undefined>();
  const [playingAudioId, setPlayingAudioId] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setSelectedPreviewId(previewLearnables[0]?.id);
  }, [previewLearnables]);

  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    [],
  );

  const effectiveSelectedPreviewId = selectedPreviewId ?? previewLearnables[0]?.id;
  const selectedPreviewLearnable = previewLearnables.find(
    learnable => learnable.id === effectiveSelectedPreviewId,
  );
  const hasMediaAside = hasPreview || isPreviewLoading;
  const shouldMountPreview = showPreviewUi && (isPreviewLoading || hasPreview);
  const usePreviewGrid = shouldMountPreview;
  const [previewSectionOpen, setPreviewSectionOpen] = useState(false);
  const [showLoadingMediaColumn, setShowLoadingMediaColumn] = useState(false);

  useEffect(() => {
    if (!shouldMountPreview) {
      setPreviewSectionOpen(false);
      return undefined;
    }

    const frameId = requestAnimationFrame(() => {
      setPreviewSectionOpen(true);
    });

    return () => cancelAnimationFrame(frameId);
  }, [shouldMountPreview]);

  useEffect(() => {
    if (!isPreviewLoading) {
      setShowLoadingMediaColumn(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoadingMediaColumn(true);
    }, PREVIEW_ANIMATION.mediaDelayMs - 80);

    return () => window.clearTimeout(timeoutId);
  }, [isPreviewLoading]);

  const showMediaColumn =
    showPreviewUi && (hasMediaAside || (isPreviewLoading && showLoadingMediaColumn));

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingAudioId(null);
  }, []);

  const handleSelectPreviewLearnable = useCallback(
    (learnableId: number) => {
      setSelectedPreviewId(learnableId);
      trackPreviewAction('clicked_wordlist_preview_item', {
        learnable_id: String(learnableId),
      });
    },
    [trackPreviewAction],
  );

  const handlePlayAudio = useCallback(
    (learnableId: number) => {
      const learnable = previewLearnables.find(entry => entry.id === learnableId);
      if (!learnable?.audioSrc) return;

      setSelectedPreviewId(learnableId);
      stopAudio();

      const audio = new Audio(learnable.audioSrc);
      audioRef.current = audio;
      setPlayingAudioId(learnableId);
      audio.onended = () => {
        setPlayingAudioId(null);
        audioRef.current = null;
      };
      void audio.play().catch(() => {
        setPlayingAudioId(null);
        audioRef.current = null;
      });
    },
    [previewLearnables, stopAudio],
  );

  const hasTrackedPreview = useRef(false);
  useEffect(() => {
    if (previewLearnables.length > 0 && !hasTrackedPreview.current) {
      hasTrackedPreview.current = true;
      trackPreviewAction('wordlist_preview_shown', {
        preview_count: String(previewLearnables.length),
      });
    }
  }, [previewLearnables.length, trackPreviewAction]);

  if (!activeWordlist) return null;

  const learnedCount = hasWordlistProgress(activeWordlist)
    ? activeWordlist.learnedLearnablesCount
    : 0;
  const totalCount = activeWordlist.learnablesCount;
  const progressPercent = totalCount > 0 ? (learnedCount / totalCount) * 100 : 0;

  const changeWordlistButton = (
    <S.ChangeWordlistButtonWrap>
      <LinkButton
        buttonSize="extraSmall"
        buttonType="secondary"
        icon="sort"
        href={useVocabTracks ? ROUTES.wordlists.changeVocabTrack : ROUTES.wordlists.main}
        onClick={handleCardClick}
        data-testid="current-wordlist-change-wordlist"
      >
        {intl.formatMessage(useVocabTracks ? vocabTrackMsgs.changeVocabList : msgs.changeWordlist)}
      </LinkButton>
    </S.ChangeWordlistButtonWrap>
  );

  const communityBadge = activeWordlist.creator === 'user' && <CommunityWordlistBadge />;

  return (
    <S.Wrapper data-testid="current-wordlist-card">
      <S.CardHeader>
        <S.CurrentWordlistLink data-testid="current-wordlist-link">
          <Link
            href={stringifyUrl(`${ROUTES.wordlists.details}/${activeWordlist.wordlistId}`, {
              languagePairId: String(languagePairId),
            })}
            onClick={handleCardClick}
            aria-label={activeWordlist.title}
            data-testid="current-wordlist-view-wordlist"
          >
            <S.CurrentWordlistImage src={activeWordlist.pictureUrl} alt="" />
          </Link>
          <S.CurrentWordlistInfo>
            {showPracticeVocabHeader ? (
              <>
                <S.PracticeVocabTitle>
                  {intl.formatMessage(msgs.practiceVocabTitle)}
                </S.PracticeVocabTitle>
                <S.CurrentWordlistTitleRow>
                  <S.CurrentWordlistSubline data-testid="current-wordlist-subline">
                    {intl.formatMessage(msgs.learningWordlist, {
                      title: activeWordlist.title,
                      bold: (chunks: ReactNode) => (
                        <S.CurrentWordlistSublineTitle>{chunks}</S.CurrentWordlistSublineTitle>
                      ),
                    })}
                  </S.CurrentWordlistSubline>
                  {communityBadge}
                  {changeWordlistButton}
                </S.CurrentWordlistTitleRow>
              </>
            ) : (
              <>
                <S.CurrentWordlistLabel>
                  {intl.formatMessage(msgs.currentlyLearning)}
                </S.CurrentWordlistLabel>
                <S.CurrentWordlistTitleRow>
                  <S.CurrentWordlistTitle>{activeWordlist.title}</S.CurrentWordlistTitle>
                  {communityBadge}
                  {changeWordlistButton}
                </S.CurrentWordlistTitleRow>
              </>
            )}
          </S.CurrentWordlistInfo>
        </S.CurrentWordlistLink>
        {totalCount > 0 && (
          <S.ListProgress>
            <S.ListProgressLabel>
              {intl.formatMessage(msgs.wordsLearnt, {
                learned: learnedCount,
                total: totalCount,
              })}
            </S.ListProgressLabel>
            <S.ListProgressTrack
              role="progressbar"
              aria-valuenow={learnedCount}
              aria-valuemin={0}
              aria-valuemax={totalCount}
              aria-label={intl.formatMessage(msgs.listProgressLabel, {
                title: activeWordlist.title,
              })}
            >
              <S.ListProgressFill $percentComplete={progressPercent} />
            </S.ListProgressTrack>
          </S.ListProgress>
        )}
      </S.CardHeader>

      <S.MainColumn
        $usePreviewGrid={usePreviewGrid}
        $showMediaColumn={showMediaColumn}
        data-preview-grid={usePreviewGrid ? 'true' : undefined}
      >
        <S.MainColumnTop>
          {shouldMountPreview && (
            <S.PreviewSectionExpand $isOpen={previewSectionOpen}>
              <S.PreviewSectionInner>
                {isPreviewLoading ? (
                  <PreviewLoadingSkeleton />
                ) : (
                  effectiveSelectedPreviewId !== undefined && (
                    <S.PreviewContentFadeIn>
                      <LocalsVideoPreview
                        learnables={previewLearnables}
                        selectedId={effectiveSelectedPreviewId}
                        onSelectLearnable={handleSelectPreviewLearnable}
                        showNextLessonTitle={showPracticeVocabHeader}
                      />
                    </S.PreviewContentFadeIn>
                  )
                )}
              </S.PreviewSectionInner>
            </S.PreviewSectionExpand>
          )}
        </S.MainColumnTop>
        {showMediaColumn && (
          <S.PreviewVideoAside>
            {isPreviewLoading ? (
              <PreviewMediaSkeleton />
            ) : (
              selectedPreviewLearnable && (
                <MediaPreviewPanel
                  videoSrc={selectedPreviewLearnable.videoSrc}
                  audioSrc={selectedPreviewLearnable.audioSrc}
                  isAudioPlaying={playingAudioId === selectedPreviewLearnable.id}
                  onPlayAudio={() => handlePlayAudio(selectedPreviewLearnable.id)}
                />
              )
            )}
          </S.PreviewVideoAside>
        )}
        <S.StartLessonButtonWrap>
          <Button
            buttonSize="medium"
            buttonType="primary"
            wide
            onClick={() => {
              void handleStartLesson();
            }}
            disabled={isAdvancingWordlist}
            data-testid="current-wordlist-start-lesson"
          >
            {intl.formatMessage(msgs.startLesson)}
          </Button>
        </S.StartLessonButtonWrap>
      </S.MainColumn>
    </S.Wrapper>
  );
};
